import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile, toBlobURL } from "@ffmpeg/util";

const BASE_URL = "https://unpkg.com/@ffmpeg/core@0.12.6/dist/esm";

export const loadFfmpeg = async (
  ffmpeg: FFmpeg,
  setProgress: (number) => void,
) => {
  let totalDurationInSeconds = 0;
  let haveParsedDuration = false;

  setProgress(1);

  ffmpeg.on("log", ({ message }) => {
    if (!haveParsedDuration) {
      const durationMatch = message.match(/Duration:\s+(\d+):(\d+):(\d+\.\d+)/);
      if (durationMatch) {
        const [, h, m, s] = durationMatch;
        totalDurationInSeconds = hmsToSeconds(h, m, s);
        haveParsedDuration = true;
      }
    }

    const timeMatch = message.match(/time=(\d+):(\d+):(\d+\.\d+)/);
    if (timeMatch && totalDurationInSeconds > 0) {
      const [, h, m, s] = timeMatch;
      const currentTimeInSeconds = hmsToSeconds(h, m, s);
      const progress = (currentTimeInSeconds / totalDurationInSeconds) * 100;
      setProgress(progress + 1);
    }
  });

  await ffmpeg.load({
    coreURL: await toBlobURL(`${BASE_URL}/ffmpeg-core.js`, "text/javascript"),
    wasmURL: await toBlobURL(
      `${BASE_URL}/ffmpeg-core.wasm`,
      "application/wasm",
    ),
  });
};

function hmsToSeconds(hours: string, minutes: string, seconds: string): number {
  const h = Number.parseInt(hours, 10);
  const m = Number.parseInt(minutes, 10);
  const s = Number.parseFloat(seconds);
  return h * 3600 + m * 60 + s;
}

export const transcode = async (
  ffmpeg: FFmpeg,
  inputMedia: File,
): Promise<File> => {
  const inputFileName = inputMedia.name;
  const outputFileName = `${inputFileName.split(".")[0]}-${Date.now()}.m4a`;

  // ffmpeg write the file to the virtual file system where it is processed
  await ffmpeg.writeFile(inputFileName, await fetchFile(inputMedia));

  await ffmpeg.exec([
    "-i",
    inputFileName,
    "-codec:a", // audio codec: aac
    "aac",
    "-b:a", // audio bitrate: 48k
    "48k",
    "-ac", // audio channels: 1 (mono)
    "1",
    "-ar", // audio sample rate: 24000 Hz
    "24000",
    "-vn", // no video
    outputFileName,
  ]);
  const data = await ffmpeg.readFile(outputFileName);

  return new File([data], inputFileName, { type: "audio/mp4" });
};
